<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import AiAgentList from './AiAgentList.vue';
import AiChatPanel from './AiChatPanel.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const selectedAgentId = computed(() => route.params.agentId);
const selectedConversationId = computed(() => route.params.conversationId);

const agents = computed(() => store.getters['aiAgents/getAgents']);
const currentAgent = computed(() => store.getters['aiAgents/getCurrentAgent']);
const currentConversation = computed(
  () => store.getters['aiAgents/getCurrentConversation']
);
const conversations = computed(
  () => store.getters['aiAgents/getConversations']
);
const messages = computed(() => store.getters['aiAgents/getMessages']);
const uiFlags = computed(() => store.getters['aiAgents/getUIFlags']);
const accountAgents = computed(() => store.getters['agents/getAgents']);

const showMobileList = ref(true);

onMounted(async () => {
  await store.dispatch('aiAgents/fetchAgents');
  store.dispatch('agents/get');
  if (selectedAgentId.value) {
    await selectAgent(selectedAgentId.value);
    if (selectedConversationId.value) {
      await store.dispatch('aiAgents/fetchConversation', {
        agentId: selectedAgentId.value,
        conversationId: selectedConversationId.value,
      });
    }
  }
});

watch(selectedAgentId, async newId => {
  if (newId) await selectAgent(newId);
});

watch(selectedConversationId, async newId => {
  if (newId && selectedAgentId.value) {
    await store.dispatch('aiAgents/fetchConversation', {
      agentId: selectedAgentId.value,
      conversationId: newId,
    });
  }
});

const selectAgent = async agentId => {
  showMobileList.value = false;
  await store.dispatch('aiAgents/fetchAgent', agentId);
  await store.dispatch('aiAgents/fetchConversations', agentId);

  if (!selectedConversationId.value) {
    router.push(
      accountScopedRoute('ai_chat_agent', { agentId: String(agentId) })
    );
  }
};

const selectConversation = async conversationId => {
  router.push(
    accountScopedRoute('ai_chat_conversation', {
      agentId: String(selectedAgentId.value),
      conversationId: String(conversationId),
    })
  );
};

const onNewConversation = async () => {
  if (!selectedAgentId.value) return;
  const conv = await store.dispatch(
    'aiAgents/createConversation',
    selectedAgentId.value
  );
  router.push(
    accountScopedRoute('ai_chat_conversation', {
      agentId: String(selectedAgentId.value),
      conversationId: String(conv.id),
    })
  );
};

const onSendMessage = async content => {
  if (!selectedAgentId.value || !selectedConversationId.value) return;
  await store.dispatch('aiAgents/sendMessage', {
    agentId: selectedAgentId.value,
    conversationId: selectedConversationId.value,
    content,
  });
};

const onDeleteConversation = async () => {
  if (!selectedAgentId.value || !selectedConversationId.value) return;
  await store.dispatch('aiAgents/deleteConversation', {
    agentId: selectedAgentId.value,
    conversationId: selectedConversationId.value,
  });
  router.push(
    accountScopedRoute('ai_chat_agent', {
      agentId: String(selectedAgentId.value),
    })
  );
};

const onAddParticipant = async userId => {
  if (!selectedAgentId.value || !selectedConversationId.value) return;
  await store.dispatch('aiAgents/addParticipant', {
    agentId: selectedAgentId.value,
    conversationId: selectedConversationId.value,
    userId,
  });
};

const onRemoveParticipant = async userId => {
  if (!selectedAgentId.value || !selectedConversationId.value) return;
  await store.dispatch('aiAgents/removeParticipant', {
    agentId: selectedAgentId.value,
    conversationId: selectedConversationId.value,
    userId,
  });
};

const onBackToList = () => {
  showMobileList.value = true;
  store.dispatch('aiAgents/clearCurrentChat');
  router.push(accountScopedRoute('ai_chat_index'));
};
</script>

<template>
  <div class="flex h-full w-full overflow-hidden bg-n-background">
    <div
      class="flex flex-col border-r border-n-weak"
      :class="{
        'hidden md:flex': !showMobileList,
        'flex w-full md:w-auto': showMobileList,
      }"
      style="width: 320px; min-width: 280px"
    >
      <AiAgentList
        :agents="agents"
        :conversations="conversations"
        :selected-agent-id="selectedAgentId"
        :selected-conversation-id="selectedConversationId"
        :is-loading="uiFlags.isFetchingAgents"
        @select-agent="selectAgent"
        @select-conversation="selectConversation"
        @new-conversation="onNewConversation"
      />
    </div>

    <div
      class="flex flex-1 flex-col"
      :class="{ 'hidden md:flex': showMobileList }"
    >
      <AiChatPanel
        v-if="currentAgent"
        :agent="currentAgent"
        :messages="messages"
        :conversation="currentConversation"
        :account-agents="accountAgents"
        :is-sending="uiFlags.isSendingMessage"
        :is-streaming="uiFlags.isStreaming"
        :is-loading="uiFlags.isFetchingMessages"
        :has-conversation="!!selectedConversationId"
        @send-message="onSendMessage"
        @new-conversation="onNewConversation"
        @delete-conversation="onDeleteConversation"
        @add-participant="onAddParticipant"
        @remove-participant="onRemoveParticipant"
        @back="onBackToList"
      />
      <div
        v-else
        class="flex flex-1 flex-col items-center justify-center gap-4 p-8"
      >
        <div
          class="flex size-16 items-center justify-center rounded-2xl bg-n-alpha-1"
        >
          <span class="i-lucide-bot-message-square text-3xl text-n-slate-11" />
        </div>
        <h2 class="text-xl font-semibold text-n-slate-12">IA Chat</h2>
        <p class="max-w-sm text-center text-sm text-n-slate-11">
          Selecione um agente IA na lateral para iniciar uma conversa. Os
          agentes conectam via webhook ao seu sistema externo.
        </p>
      </div>
    </div>
  </div>
</template>
